import { APP_CONFIG } from '../config.js';

export const FALLBACK_LOCALE = 'en';

export const DEFAULT_MESSAGES = {
  en: {
    loading: 'Loading...',
    guestMode: 'Guest Mode - Limited Usage',
    usageThisMonth: 'Usage This Month',
    requests: 'requests',
    remainingThisMonth: 'Only {count} requests remaining this month',
    readyTitle: 'Ready to generate',
    readyDescription: 'Enter a prompt above to get started with AI-powered completions.',
    poweredBy: 'Powered by',
    guestFooter: 'Sign in for unlimited access and premium features',
    quotaExceeded: 'You have reached your quota limit. Please upgrade your plan to continue.',
    guestQuotaExceeded: 'You have reached the daily limit for guest users. Please sign in to continue.',
    paymentRequired: 'Free usage is exhausted. Sign in or upgrade to continue.',
    generationFailed: 'Failed to generate completion. Please try again.',
    copyFailed: 'Failed to copy to clipboard',
    generating: 'Generating...',
    clear: 'Clear',
    tryExamples: 'Try these examples:',
    promptTooLong: 'Prompt is too long. Please keep it under {maxLength} characters.',
    noResponse: 'No response generated.',
    copied: 'Copied!',
    copy: 'Copy',
    share: 'Share',
    input: 'Input:',
    output: 'Output:',
    tokens: 'tokens',
    time: 'Time:',
    signInRequired: 'Sign In Required',
    signInRequiredBody: 'Please sign in to access this feature.',
    guestLimitBody: "You've reached the daily limit for guest users. Please sign in to continue using the service.",
    signInToContinue: 'Sign In to Continue',
    welcome: 'Welcome!',
    guestChoiceBody: 'Sign in for unlimited access, or continue as a guest with limited usage.',
    guestModeLabel: 'Guest Mode:',
    guestRequestsRemaining: '{count} requests remaining today',
    signInFullAccess: 'Sign In for Full Access',
    continueAsGuest: 'Continue as Guest ({count} left)',
    upgradeBenefits: 'Upgrade Benefits',
    premiumFeatures: 'Premium Features',
    unlimitedRequests: 'Unlimited requests',
    prioritySupport: 'Priority support',
    advancedFeatures: 'Advanced features',
    usageAnalytics: 'Usage analytics',
  },
  'zh-CN': {
    loading: '加载中...',
    guestMode: '访客模式 - 使用次数有限',
    usageThisMonth: '本月用量',
    requests: '次请求',
    remainingThisMonth: '本月仅剩 {count} 次请求',
    readyTitle: '可以开始生成',
    readyDescription: '在上方输入内容，开始使用 AI 生成结果。',
    poweredBy: '由',
    guestFooter: '登录后可获得不限次数访问和高级功能',
    quotaExceeded: '你已达到用量限制，请升级方案后继续。',
    guestQuotaExceeded: '访客今日使用次数已达上限，请登录后继续。',
    paymentRequired: '免费额度已用完，请登录或充值后继续。',
    generationFailed: '生成失败，请重试。',
    copyFailed: '复制到剪贴板失败',
    generating: '生成中...',
    clear: '清除',
    tryExamples: '试试这些示例：',
    promptTooLong: '输入过长，请控制在 {maxLength} 个字符以内。',
    noResponse: '没有生成结果。',
    copied: '已复制',
    copy: '复制',
    share: '分享',
    input: '输入：',
    output: '输出：',
    tokens: 'token',
    time: '耗时：',
    signInRequired: '需要登录',
    signInRequiredBody: '请登录后使用此功能。',
    guestLimitBody: '访客今日使用次数已达上限，请登录后继续使用。',
    signInToContinue: '登录后继续',
    welcome: '欢迎！',
    guestChoiceBody: '登录可获得不限次数访问，也可以先以访客身份继续。',
    guestModeLabel: '访客模式：',
    guestRequestsRemaining: '今日剩余 {count} 次请求',
    signInFullAccess: '登录获取完整访问',
    continueAsGuest: '以访客身份继续（剩余 {count} 次）',
    upgradeBenefits: '升级权益',
    premiumFeatures: '高级功能',
    unlimitedRequests: '不限次数请求',
    prioritySupport: '优先支持',
    advancedFeatures: '高级功能',
    usageAnalytics: '用量分析',
  },
};

export function normalizeLocale(locale) {
  const normalized = String(locale || '').replace('_', '-').toLowerCase();
  if (['zh', 'zh-cn', 'zh-hans', 'cn'].includes(normalized)) return 'zh-CN';
  if (['en', 'en-us', 'en-gb'].includes(normalized)) return 'en';
  return FALLBACK_LOCALE;
}

export function createTranslator(locale = FALLBACK_LOCALE, messages = {}) {
  const activeLocale = normalizeLocale(locale);
  const dictionaries = Object.entries(messages || {}).reduce(
    (acc, [messageLocale, dictionary]) => {
      if (!dictionary || typeof dictionary !== 'object') return acc;
      const normalizedLocale = normalizeLocale(messageLocale);
      acc[normalizedLocale] = {
        ...(acc[normalizedLocale] || {}),
        ...dictionary,
      };
      return acc;
    },
    {
      en: { ...DEFAULT_MESSAGES.en },
      'zh-CN': { ...DEFAULT_MESSAGES['zh-CN'] },
    },
  );
  return (key, values = {}) => {
    const template = dictionaries[activeLocale]?.[key] || dictionaries[FALLBACK_LOCALE]?.[key] || key;
    return template.replace(/\{(\w+)\}/g, (_, token) => String(values[token] ?? ''));
  };
}

export const activeLocale = normalizeLocale(APP_CONFIG.LOCALE);
export const t = createTranslator(activeLocale, APP_CONFIG.I18N_MESSAGES);
